import HttpMethod from "./HttpMethod";
import { BASE_BACKEND_URL } from "../api/config";

class HttpApis {
  constructor() {
    this.baseUrl = BASE_BACKEND_URL;
    this.pendingRequests = new Map();
  }
  // Helper method to get auth token from persisted Redux store
  getAuthToken() {
    try {
      const authData = JSON.parse(localStorage.getItem("authData"));
      return authData?.token || null;
    } catch {
      return null;
    }
  }

  // Helper to merge headers with auth token if available
  getHeaders(customHeaders = {}) {
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...customHeaders
    };
    const authToken = this.getAuthToken();
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }
    return headers;
  }
  async _deduplicatedRequest(method, endpoint, params, customHeaders, fetcher) {
    const requestKey = `${method}:${endpoint}:${JSON.stringify(params)}`;

    if (this.pendingRequests.has(requestKey)) {
      return this.pendingRequests.get(requestKey);
    }

    const requestPromise = (async () => {
      try {
        return await fetcher();
      } finally {
        this.pendingRequests.delete(requestKey);
      }
    })();

    this.pendingRequests.set(requestKey, requestPromise);
    return requestPromise;
  }

  async get(endpoint, params = '', customHeaders = {}) {
    return this._deduplicatedRequest('GET', endpoint, params, customHeaders, () =>
      HttpMethod._get(`${this.baseUrl}${endpoint}`, params, this.getHeaders(customHeaders))
    );
  }
  async post(endpoint, params = {}, customHeaders = {}) {
    return this._deduplicatedRequest('POST', endpoint, params, customHeaders, () =>
      HttpMethod._post(`${this.baseUrl}${endpoint}`, params, this.getHeaders(customHeaders))
    );
  }

  async delete(endpoint, params = {}, customHeaders = {}) {
    return this._deduplicatedRequest('DELETE', endpoint, params, customHeaders, () =>
      HttpMethod._delete(`${this.baseUrl}${endpoint}`, params, this.getHeaders(customHeaders))
    );
  }

  // Content Related APIs
  async getMenuList(params) {
    return this.get('client/v1/home', params);
  }
  async getSection(slug) {
    return this.get(`client/v1/content/${slug}`);
  }
  async getSeriesList(limit = 1000) {
    return this.get(`client/v1/series?limit=${limit}`);
  }
  async getMoviesList(limit = 1000) {
    return this.get(`client/v1/movies?limit=${limit}`);
  }

  // has_content=true → only creators who have published content (see backend
  // getCreatorsList). The public directory should never list empty creators.
  async getCreatorList(limit = 1000) {
    return this.get(`client/v1/creators/list?limit=${limit}&has_content=true`);
  }
  // Paginated + searchable creators list. `search` is matched server-side
  // (name / channel / category / email). `perpage` drives the page size used
  // for "load more" (the endpoint takes page + perpage).
  async getCreatorsPaged({ page = 1, perpage = 24, search = "" } = {}) {
    const q = `page=${page}&perpage=${perpage}&has_content=true` +
      (search ? `&search=${encodeURIComponent(search)}` : "");
    return this.get(`client/v1/creators/list?${q}`);
  }
  // Creator content (GET client/v1/creators/:id/content). Backwards-compatible:
  // called as getCreatorListbycontentId(id) it behaves exactly as before
  // (?limit=1000, no page/type). Passing { page, type } adds the server-side
  // pagination + bucket params the creator page's infinite scroll uses.
  // `type` is the backend bucket: 'all' | 'videos' | 'series' (there is no
  // 'movies'/'other' bucket — Movie vs Short-Clip both live under 'videos', so
  // that split is done client-side). With pagination, the caller detects
  // "end of list" by a page shorter than `limit` (the endpoint sends no total).
  async getCreatorListbycontentId(id, { limit = 1000, page, type } = {}) {
    let q = `limit=${limit}`;
    if (page != null) q += `&page=${page}`;
    if (type) q += `&type=${encodeURIComponent(type)}`;
    return this.get(`client/v1/creators/${id}/content?${q}`);
  }
  async getCreatorListbyId(id) {
    return this.get(`client/v1/creators/${id}`);
  }
  async getList(limit = 1000) {
    return this.get(`client/v1/my-list?limit=${limit}`);
  }
  async addToList(slug) {
    return this.post(`client/v1/my-list/${slug}`);
  }
  async deleteToList(slug) {
    return this.delete(`client/v1/my-list/${slug}`);
  }

  async getSearchResults(query) {
    return this.get(`client/v1/search?query=${encodeURIComponent(query)}`);
  }

  async getRecentSearchResults() {
    return this.get(`client/v1/recent-search`);
  }

  async postRecentSearchResults(content_type, content_id) {
    const formData = new FormData();
    formData.append("content_type", content_type);
    formData.append("content_id", content_id);
    return this.post("client/v1/recent-search", formData);
  }

  async getChanelsWithScheduls() {
    return this.get('active-channels-with-schedules');
  }
  async getConfig() {
    return this.get('v1/config');
  }
  // Published legal page (Terms / Privacy / Cookie). 404 when unpublished.
  async getLegalDocument(type) {
    return this.get(`v1/config/legal/${encodeURIComponent(type)}`);
  }
  async getAds() {
    return this.get('v1/ads');
  }
  // Authentication Related APIs
  async Login(params) {
    return this.post('client/v1/subscribers/login', params);
  }
  async signUp(params) {
    return this.post('client/v1/subscribers/register', params);
  }
  async signOut(params) {
    return this.post('client/v1/subscribers/logout', params);
  }
  async refreshToken(refreshToken) {
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Authorization': '',
    };
    return HttpMethod._post(
      `${this.baseUrl}client/v1/refresh-token`,
      { refresh_token: refreshToken },
      headers
    );
  }
  async accessCode(params, config = {}) {
    return this.post('client/v1/activation-code/confirm', params, config);
  }

  async Analytics(params, config = {}) {
    return this.post('client/v1/app-event', params, config);
  }

  // User Preferences APIs
  async postAddTofavourite(slug, customHeaders = {}) {
    return this.post(
      `client/v1/like-dislike/${slug}`,
      { action: "like" },
      customHeaders
    );
  }

  async postRemovefavourite(slug, customHeaders = {}) {
    return this.post(
      `client/v1/like-dislike/${slug}`,
      { action: "dislike" },
      customHeaders
    );
  }

  async getWatchList() {
    return this.get('client/v1/liked');
  }
    async getGenrebyId(genre_id) {
    return this.get(`client/v1/genre-members/${genre_id}`);
  }
  // Watching History APIs
  async postContinueWatching(params) {
    return this.post('client/v1/continue-watching', params);
  }
  async getContinueWatchingList({ limit = 1000, headers = {} } = {}) {
    return this.get(`client/v1/continue-watching?limit=${limit}`, { headers });
  }
  // Streaming session — feeds the watch-time ledger (heartbeat credits watched
  // seconds). Same endpoints as the main app. Device-limit is governed by the
  // backend's default_max_devices setting (set high for this project).
  async streamStart(params) {
    return this.post('client/v1/stream/start', params);
  }
  async streamHeartbeat(params) {
    return this.post('client/v1/stream/heartbeat', params);
  }
  async streamStop(params) {
    return this.post('client/v1/stream/stop', params);
  }
  async getCastAndCrew(id) {
    return this.get(`client/v1/cast-crew-members/${id}`);
  }

async getContentEntitlements(accessType = 'rent') {
  return this.get(`client/v1/content-entitlements/mine?access_type=${accessType}`);
}
async getContentEntitlementsall() {
  return this.get(`client/v1/content-entitlements/mine`);
}
// Server-authoritative "does this user own this content right now?" — used by
// the buy/rent thank-you page to confirm a real, paid entitlement exists
// (written by the Stripe webhook) before showing success. Returns
// rent_entitlement / buy_entitlement for movies, ownership fields for series.
async getEntitlementStatus(slug) {
  return this.get(
    `client/v1/content-entitlements/status?slug=${encodeURIComponent(slug)}`
  );
}
// Resolves the real Stripe receipt (hosted receipt_url + a clean ch_/pi_
// receipt id) for a completed buy/rent checkout. Used by the buy/rent
// thank-you page so "View Receipt" actually links to Stripe and the shown
// transaction id isn't the long cs_ session id. Ownership-gated server-side.
async getEntitlementReceipt(sessionId) {
  return this.get(
    `client/v1/content-entitlements/receipt?session_id=${encodeURIComponent(sessionId)}`
  );
}

  async getAllProfile() {
    return this.get(`client/v1/subscribers/profiles`);
  }

  async getAllAvatarImg() {
    return this.get(`client/v1/avatars`);
  }

  async postSetProfile(profile_id) {
    return this.post(`client/v1/subscribers/profiles/set/${profile_id}`);
  }

  async postUpdateProfile(profile_id, body) {
    return this.post(`client/v1/subscribers/profiles/${profile_id}`, body);
  }

  async deleteProfile(profile_id) {
    return this.delete(`client/v1/subscribers/profiles/${profile_id}`);
  }
  async getSingleProfile(profile_id) {
    return this.get(`client/v1/subscribers/profiles/${profile_id}`);
  }

  async postCreateProfile(body) {
    return this.post(`client/v1/subscribers/profiles/create`, body);
  }

  //section Manager
  async getSectionMenu() {
    return this.get('client/v1/sections-menu');
  }
  async getSectinBySlug(slug) {
    return this.get(`client/v1/sections-menu/${slug}`);
  }

  // Subscription & Payment APIs
  async getSubscriptionPlans() {
    return this.get('v1/subscription-plans/public');
  }
  async purchase(params) {
    return this.post('client/v1/purchase', params);
  }
  async deleteSubcription(params) {
    return this.post('client/v1/purchase/cancel-subscription-web', params);
  }
  async reactivateSubscription(params) {
    // Reverse a pending cancellation. Returns 409 if Stripe has already
    // fully ended the sub — caller should prompt repurchase in that case.
    return this.post('client/v1/purchase/uncancel-subscription-web', params);
  }
  async getMySubscription() {
    // Server-authoritative view of the current user's subscription.
    // Always prefer this over localStorage — localStorage can lag behind
    // backend state (cron downgrades, webhook cancellations).
    return this.get('client/v1/subscribers/subscription');
  }
  async createCheckoutSession(plan_price_id) {
    // Per-user Stripe Checkout Session. Server decides trial eligibility:
    // if user already trialed before, the returned session charges them
    // immediately (no trial), and response.data.trial_skipped is true.
    return this.post('client/v1/purchase/create-checkout-session', { plan_price_id });
  }
  async createTvodCheckoutSession(slug, accessType, cancelUrl) {
  return this.post('client/v1/content-entitlements/stripe-checkout', {
    slug,
    access_type: accessType,
    // Where Stripe sends the user if they hit its back/cancel button. We send
    // the page they started from (the content details page) so a cancel does
    // NOT land them on the buy-rent thank-you page, which always renders a
    // (fake) "Payment Successful!". Omitted → backend falls back to /home.
    ...(cancelUrl ? { cancel_url: cancelUrl } : {}),
  });
}
  async getTrialEligibility() {
    // Server-authoritative answer to "is this user eligible for a free trial?"
    // Used to render an honest CTA on the Subscription page (no trial pitch
    // for returning customers).
    return this.get('client/v1/purchase/trial-eligibility');
  }
  async getSwitchPlanPreview(plan_price_id) {
    // Returns exact charge amount, switch kind, and human-readable note for
    // confirmation modal. Server hits Stripe's upcoming-invoice API for the
    // real prorated number.
    return this.get(
      `client/v1/purchase/switch-plan/preview?plan_price_id=${encodeURIComponent(plan_price_id)}`
    );
  }
  async switchPlan(plan_price_id) {
    // Executes the actual plan switch. Stripe handles money math; webhook
    // reconfirms our DB after the switch completes.
    return this.post('client/v1/purchase/switch-plan', { plan_price_id });
  }
  async createPortalSession(return_url) {
    // Returns a short-lived Stripe Customer Portal URL. Caller redirects
    // the browser to that URL; Stripe hosts the page; user can update card,
    // view invoices, etc. Configured one-time in Stripe Dashboard.
    return this.post('client/v1/purchase/portal-session', { return_url });
  }



  //password
  async forgotPassowrd(params) {
    return this.post('client/v1/subscribers/forgot-password', params);
  }

  async verifyOtp(params) {
    return this.post('client/v1/subscribers/verify-otp', params);
  }

  // Signup email verification (distinct from the password-reset OTP above)
  async verifyEmail(params) {
    return this.post('client/v1/subscribers/verify-email', params);
  }

  async resendVerification(params) {
    return this.post('client/v1/subscribers/resend-verification', params);
  }

  async resetPassword(params) {
    return this.post('client/v1/subscribers/reset-password', params);
  }
  async changePassword(params) {
    return this.post('client/v1/subscribers/change-password', params);
  }

  // One-click unsubscribe from marketing (new-release) email. `params` is
  // { token } — the signed token from the email link IS the credential, no
  // auth header required/used.
  async unsubscribe(params) {
    return this.post('client/v1/subscribers/unsubscribe/confirm', params);
  }

  // tv-activation-api //
  async tvActivation(body, headers) {
    return await HttpMethod._post(
      `${BASE_BACKEND_URL}tv/link-activation-code`,
      body,
      headers
    );
  }

  /* ===================== CREATOR MONETIZATION ===================== */
  // baseUrl ends with `/api/` so creator routes => `v1/creator/monetization/...`,
  // client routes => `client/v1/...`.

  // Creator dashboard (auth: Creator)
  async getCreatorOverview() {
    return this.get('v1/creator/monetization/overview');
  }
  async getCreatorReferral() {
    return this.get('v1/creator/monetization/referral');
  }
  async getCreatorReferralList(params = '') {
    return this.get(`v1/creator/monetization/referral/list${params ? `?${params}` : ''}`);
  }
  async getCreatorContentAnalytics(year, month) {
    const q = new URLSearchParams();
    if (year) q.append('year', year);
    if (month) q.append('month', month);
    return this.get(`v1/creator/monetization/content-analytics?${q.toString()}`);
  }
  async getCreatorRevenue(year) {
    const q = year ? `?year=${year}` : '';
    return this.get(`v1/creator/monetization/revenue${q}`);
  }
  async getCreatorPayouts() {
    return this.get('v1/creator/monetization/payouts');
  }
  async getCreatorPayoutAccount() {
    return this.get('v1/creator/monetization/payout-account');
  }
  async startCreatorOnboarding(params = {}) {
    return this.post('v1/creator/monetization/payout-account/onboard', params);
  }
  async refreshCreatorOnboarding() {
    return this.post('v1/creator/monetization/payout-account/refresh', {});
  }

  // Referral tracking (public)
  async trackReferralClick(params) {
    return this.post('client/v1/referral/track-click', params);
  }
  async resolveReferralCode(code) {
    return this.get(`client/v1/referral/resolve/${encodeURIComponent(code)}`);
  }

  // Returns the logged-in user's referral attribution (or null).
  async getMyReferralAttribution() {
    return this.get('client/v1/referral/me');
  }
}

// Create a singleton instance
const httpApis = new HttpApis();
Object.freeze(httpApis);
export default httpApis;